import { GWCA } from '~/lib/ca-constants';
import { GWUtils } from '~/utils/ca-utils';

export class GWCustomerAccountsTab extends HTMLElement {
  constructor() {
    super();
    this.tabData = this._parseTabData();
    this.tabId = this.tabData?.id;
    this.tabGroup = this.closest('sl-tab-group');
    this.tabPanel = this.closest('sl-tab-panel');
    this.mainDrawerLabel = GWUtils.appShadow.querySelector('[data-main-drawer-label]');
  }

  connectedCallback() {
    // Add Fade In Animations
    this._addAnimations();

    // Listen for tab show event
    this.tabGroup.addEventListener('sl-tab-show', (event) => {
      if (event.detail.name === this.tabId) {
        this._playFadeInAnimations();
        this._updateMainDrawerLabel();
        this._updateSignInBlockHeading();
        this._updateHash();

        GWUtils.app.reinitializeCarousels();
      } else {
        this._resetAnimations();
      }
    });

    // Initial Fade In Animations if tab is active on load
    // If the hash is not in the map, prevent animations from playing
    if (!GWCA.MAP[window.location.hash]) return;

    const isCurrentTab = window.location.hash === this.tabId;
    const isSubTabOpenInCurrentTab = GWCA.MAP[window.location.hash].includes(this.tabId);

    if (isCurrentTab || isSubTabOpenInCurrentTab) {
      this._playFadeInAnimations();
    }
  }

  /**
   * Returns all animation elements in the tab panel.
   * @returns {Array} - The animation elements.
   * @readonly
   */
  get animationElements() {
    const animationElements = this.querySelectorAll('sl-animation');
    const allAnimationElements = [...animationElements];

    const signInBlockAnimationElement = GWUtils.appShadow.querySelector('[gw-sign-in-block-animation]');
    if (signInBlockAnimationElement) {
      allAnimationElements.unshift(signInBlockAnimationElement);
    }

    return allAnimationElements;
  }

  /**
   * Parses the tab data which is a JSON string in script tag.
   * @returns {Object} - The parsed tab data.
   */
  _parseTabData() {
    const tabData = this.querySelector('[data-tab-panel-data]');

    if (tabData) {
      return JSON.parse(tabData.textContent);
    }

    return {};
  }

  /**
   * Updates the hash in the URL to match the tab's hash.
   * @returns {void}
   */
  _updateHash() {
    // If the tab has a sub-tab, do not update the hash
    const hash = window.location.hash;
    const hasSubTab = GWCA.MAP[hash]?.includes('##');
    if (hasSubTab) return;

    window.location.hash = this.tabData?.hash;
  }

  /**
   * Updates the main drawer label with the tab's title.
   * @returns {void}
   */
  _updateMainDrawerLabel() {
    const tabTitle = this.tabData?.label;

    if (tabTitle && this.mainDrawerLabel) {
      this.mainDrawerLabel.textContent = tabTitle;
    }
  }

  /**
   * Updates the sign-in block heading with the tab's sign-in block heading.
   * @returns {void}
   */
  _updateSignInBlockHeading() {
    const signInBlockHeading = this.tabData?.signInBlockHeading;
    const $signInBlockHeading = GWUtils.appShadow.querySelector('[data-sign-in-block-heading]');

    if (signInBlockHeading && $signInBlockHeading) {
      $signInBlockHeading.textContent = signInBlockHeading;
    }
  }

  /**
   * Plays the fade in animations for the tab panel.
   * @returns {void}
   */
  _playFadeInAnimations() {
    this.animationElements.forEach((element) => (element.play = true));
  }

  /**
   * Adds fade in animations to the tab panel elements.
   * @returns {void}
   */
  _addAnimations() {
    this.animationElements.forEach((element, index) => {
      element.keyframes = [
        {
          offset: 0,
          opacity: '0',
          transform: 'translate3d(0, -10px, 0)',
          easing: 'ease-in-out',
        },
        {
          offset: 1,
          opacity: '1',
          transform: 'translate3d(0, 0, 0)',
          easing: 'ease-in-out',
        },
      ];

      element.delay = index * 80;
    });
  }

  /**
   * Resets all animations in the tab panel.
   * @returns {void}
   */
  _resetAnimations() {
    this.animationElements.forEach((element) => {
      if (element.currentTime > 0) {
        element.currentTime = 0;
      }
    });
  }
}
